import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Box,
  Button,
  ThemeProvider,
  CssBaseline,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
} from "@mui/material";
import { theme } from "../theme";
import MateriomAIContainer from "../components/Containers/MateriomAIContainer";
import Authenticated from "../components/Layouts/Authenticated";
import { ChatMode, ChatModeValues } from "../types/frontend";
import { useChatContext } from "../contexts/ChatContext";
import { useSourceContext } from "../contexts/SourcesContext";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { useIsEnterprise } from "../hooks/useIsEnterprise";
import { GlitterIcon } from "../components/ui/GlitterIcon";
import { usePostHogSurvey } from "../hooks/usePostHogSurvey";
import MultiStepSurvey from "../components/Survey/MultiStepSurvey";
import {
  MATERIOM_AI_V2_FEEDBACK_SURVEY,
  MATERIOM_AI_V2_ONBOARDING_SURVEY,
} from "../constants/survey";
import { useSavedChats } from "../hooks/useSavedChats";

/**
 * Default number of hours until the survey can be shown again after submission/dismissal.
 */
const MATERIOM_AI_V2_FEEDBACK_SURVEY_SHOW_AFTER_SUBMISSION_HOURS = 48;

/**
 * Props for the ModeToggleButton component.
 *
 * @property {boolean} isAgentMode - Indicates if the current mode is agent mode (v2).
 * @property {() => void} onOpenSwitchModal - Callback function to open the switch mode confirmation modal.
 */
interface ModeToggleButtonProps {
  isAgentMode: boolean;
  onOpenSwitchModal: () => void;
}

/**
 * ModeToggleButton component
 *
 * Renders a button for toggling between Materiom AI v1 (workflow mode) and v2 (agent mode).
 * Clicking the button triggers a modal to confirm the switch.
 *
 * @param {object} props
 * @param {boolean} props.isAgentMode - Whether the current mode is agent mode (v2)
 * @param {Function} props.onOpenSwitchModal - Function to open the switch mode confirmation modal
 * @returns {JSX.Element}
 */
function ModeToggleButton({
  isAgentMode,
  onOpenSwitchModal,
}: ModeToggleButtonProps) {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "flex-end",
        width: "100%",
        mb: 1,
      }}
    >
      <Button
        disableRipple
        variant="text"
        size="small"
        onClick={onOpenSwitchModal}
        sx={{
          textTransform: "none",
          textDecoration: "underline",
          "&:hover": {
            textDecoration: "underline",
          },
          fontSize: "16px",
        }}
      >
        {isAgentMode ? (
          "Switch to Materiom AI v1"
        ) : (
          <>
            <GlitterIcon sx={{ fontSize: 24, mr: 1 }} />
            Try Materiom AI v2
          </>
        )}
      </Button>
    </Box>
  );
}

/**
 * Props for the SwitchModeModal component.
 *
 * @property {boolean} open - Whether the modal dialog is open.
 * @property {boolean} isAgentMode - Indicates if the current chat mode is agent mode (v2).
 * @property {() => void} onClose - Callback function to close the modal dialog.
 * @property {() => void} onConfirm - Callback function to confirm the mode switch.
 */
interface SwitchModeModalProps {
  open: boolean;
  isAgentMode: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

/**
 * SwitchModeModal component
 *
 * Displays a confirmation dialog when the user attempts to switch between Materiom AI v1 and v2.
 * Informs the user that switching will clear their current chat history.
 *
 * @param {object} props
 * @param {boolean} props.open - Whether the modal is open
 * @param {boolean} props.isAgentMode - Whether the current mode is agent mode (v2)
 * @param {Function} props.onClose - Function to close the modal
 * @param {Function} props.onConfirm - Function to confirm the mode switch
 * @returns {JSX.Element}
 */
function SwitchModeModal({
  open,
  isAgentMode,
  onClose,
  onConfirm,
}: SwitchModeModalProps) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        {isAgentMode
          ? "Switch to Materiom AI v1?"
          : "Switch to Materiom AI v2?"}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1">
          This will clear your current chat history. Are you sure you want to
          continue?
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disableRipple>
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          color="primary"
          disableRipple
        >
          Yes
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Feature flag name for the Materiom AI v2 feature flag
 * @constant
 */
const MATERIOM_AI_V2_FEATURE_FLAG_NAME = "materiom-ai-v2-feature-flag";

/**
 * localStorage key for persisting the user's chat mode preference
 * @constant
 */
const CHAT_MODE_STORAGE_KEY = "materiom-ai.chatMode";

/**
 * Retrieves the stored chat mode preference from localStorage
 * @returns {ChatMode | null} The stored mode or null if not found/invalid
 */
const getStoredChatMode = (): ChatMode | null => {
  try {
    const stored = localStorage.getItem(CHAT_MODE_STORAGE_KEY);
    if (stored === ChatModeValues.Agent || stored === ChatModeValues.Workflow) {
      return stored;
    }
    return null;
  } catch (error) {
    console.warn(
      `Failed to retrieve ${CHAT_MODE_STORAGE_KEY} from localStorage:`,
      error
    );
    return null;
  }
};

/**
 * Stores the chat mode preference in localStorage
 * @param {ChatMode} mode - The mode to store
 */
const storeChatMode = (mode: ChatMode): void => {
  try {
    localStorage.setItem(CHAT_MODE_STORAGE_KEY, mode);
  } catch (error) {
    console.warn(
      `Failed to store ${CHAT_MODE_STORAGE_KEY} in localStorage:`,
      error
    );
  }
};

/**
 * AuthenticatedMateriomAI Component
 *
 * The main component for the Materiom AI application.
 * This component is protected by the Authenticated layout and the Protected Route, ensuring only
 * authenticated users can access it. It also manages chat input state, chat mode state,
 * and provides the ChatProvider context to the MateriomAIContainer.
 *
 * @returns {JSX.Element} The rendered AuthenticatedMateriomAI component.
 */
function AuthenticatedMateriomAI() {
  // Feature flag controlling access to agent mode
  // undefined = loading, true = enabled, false = disabled
  const featureFlagValue = useFeatureFlagEnabled(
    MATERIOM_AI_V2_FEATURE_FLAG_NAME
  );

  // Enterprise access: hostname check + PostHog cohort flag
  const { isEnterprise, isFlagLoading: isEnterpriseFlagLoading } =
    useIsEnterprise();

  const isFeatureFlagLoaded =
    featureFlagValue !== undefined && !isEnterpriseFlagLoading;
  // Enterprise users always have agent mode accessible
  const isAgentModeAccessible = featureFlagValue === true || isEnterprise;

  // Read mode from URL param
  const [searchParams, setSearchParams] = useSearchParams();

  // Get initial mode from URL param, falling back to stored preference
  // Precedence: URL param > localStorage > default (workflow)
  // Access control is enforced via effects once feature flag loads
  const getInitialMode = (): ChatMode => {
    const urlMode = searchParams.get("mode");
    if (urlMode === ChatModeValues.Agent) {
      return ChatModeValues.Agent;
    }
    // If no URL param, check localStorage for stored preference
    const storedMode = getStoredChatMode();
    if (storedMode === ChatModeValues.Agent) {
      return ChatModeValues.Agent;
    }
    return ChatModeValues.Workflow;
  };

  // State for the chat input
  const [chatInput, setChatInput] = useState("");
  // State for the chat mode
  const [mode, setMode] = useState<ChatMode>(getInitialMode);
  // State for the switch mode confirmation modal
  const [isSwitchModeModalOpen, setIsSwitchModeModalOpen] = useState(false);
  // Derived state for convenience
  const isAgentMode = mode === ChatModeValues.Agent;

  // Context hooks to manage chat and source states
  const {
    messages: storeMessages,
    clearMessages,
    sessionId,
  } = useChatContext();
  const { clearSources, clearAttachedSources } = useSourceContext();

  // Hook for managing saved chats and loading sessions
  const { handleOpenChat } = useSavedChats();

  // Get sessionId from URL query params
  const urlSessionId = searchParams.get("sessionId");

  // Track the sessionId that was last loaded to prevent duplicate loads
  // and to detect navigation to different sessions
  const loadedSessionIdRef = useRef<string | null>(null);
  // Track the previous message count to detect first message sent
  const prevMessageCountRef = useRef(storeMessages.length);

  // Log if survey IDs are empty
  useEffect(() => {
    if (process.env.REACT_APP_MATERIOM_AI_V2_FEEDBACK_SURVEY_ID === undefined) {
      console.log("[Warning] Materiom AI v2 Feedback survey ID is not set");
    }
    if (
      process.env.REACT_APP_MATERIOM_AI_V2_ONBOARDING_SURVEY_ID === undefined
    ) {
      console.log("[Warning] Materiom AI v2 Onboarding survey ID is not set");
    }
  }, []);

  // Materiom AI v2 Onboarding survey hook
  // Shows once for new users when they visit agent mode
  // Never expires (shown only once per user)
  const {
    survey: agentOnboardingSurvey,
    responses: onboardingResponses,
    setResponse: setOnboardingResponse,
    submitSurvey: submitOnboardingSurvey,
    dismissSurvey: dismissOnboardingSurvey,
    isVisible: isOnboardingSurveyVisible,
  } = usePostHogSurvey({
    surveyId: process.env.REACT_APP_MATERIOM_AI_V2_ONBOARDING_SURVEY_ID ?? "",
    recheckTrigger: isAgentMode ? 1 : 0, // Re-check when entering agent mode
    expiryHours: null, // Never expires - survey shown only once
  });

  // Materiom AI v2 Feedback survey hook
  // Pass message count as recheckTrigger to re-check survey conditions when messages change
  // Survey expires after 2 days (48 hours) before showing again
  const {
    survey: agentExitSurvey,
    responses: feedbackResponses,
    setResponse: setFeedbackResponse,
    submitSurvey: submitFeedbackSurvey,
    dismissSurvey: dismissFeedbackSurvey,
    isVisible: isFeedbackSurveyVisible,
  } = usePostHogSurvey({
    surveyId: process.env.REACT_APP_MATERIOM_AI_V2_FEEDBACK_SURVEY_ID ?? "",
    recheckTrigger: storeMessages.length,
    expiryHours: MATERIOM_AI_V2_FEEDBACK_SURVEY_SHOW_AFTER_SUBMISSION_HOURS,
    sessionId,
  });

  // Track previous URL mode to detect external navigation (e.g., from AgentTrialModal)
  const prevUrlModeRef = useRef<string | null>(searchParams.get("mode"));

  // Track whether we've done the initial persistence
  // This prevents overwriting stored preference when redirected to /materiom-ai after login
  const hasPersistedInitialModeRef = useRef(false);

  // Persist mode to localStorage when:
  // 1. URL has ?mode=agent on initial mount (user clicked popup/button)
  // 2. User explicitly switches modes after initial load
  useEffect(() => {
    if (isFeatureFlagLoaded && isAgentModeAccessible) {
      const urlMode = searchParams.get("mode");
      if (!hasPersistedInitialModeRef.current) {
        // First time: only persist if URL explicitly has ?mode=agent
        // This handles the popup/button navigation case
        if (urlMode === ChatModeValues.Agent) {
          storeChatMode(ChatModeValues.Agent);
        }
        hasPersistedInitialModeRef.current = true;
      } else {
        // After initial load: persist any mode change (user switched modes)
        storeChatMode(mode);
      }
    }
  }, [mode, isFeatureFlagLoaded, isAgentModeAccessible, searchParams]);

  // Restore mode from localStorage when feature flag loads
  // This handles the case where user logs in and is redirected to /materiom-ai
  // without ?mode=agent, but has a stored preference for agent mode
  useEffect(() => {
    if (isFeatureFlagLoaded && isAgentModeAccessible) {
      const urlMode = searchParams.get("mode");
      // Only restore from localStorage if URL doesn't already have a mode param
      // and the current mode is not already agent
      if (urlMode !== ChatModeValues.Agent && mode !== ChatModeValues.Agent) {
        const storedMode = getStoredChatMode();
        if (storedMode === ChatModeValues.Agent) {
          setMode(ChatModeValues.Agent);
        }
      }
    }
  }, [isFeatureFlagLoaded, isAgentModeAccessible, searchParams, mode]);

  // Force Workflow mode when agent mode is not accessible (only after flag has loaded)
  useEffect(() => {
    if (
      isFeatureFlagLoaded &&
      !isAgentModeAccessible &&
      mode === ChatModeValues.Agent
    ) {
      setMode(ChatModeValues.Workflow);
    }
  }, [isFeatureFlagLoaded, isAgentModeAccessible, mode]);

  // Force Agent mode for enterprise users (always agentic)
  useEffect(() => {
    if (isEnterprise && mode !== ChatModeValues.Agent) {
      setMode(ChatModeValues.Agent);
    }
  }, [isEnterprise, mode]);

  // Sync mode state FROM URL when navigating to agent mode (e.g., from AgentTrialModal)
  // This handles the case where the component is already mounted and URL changes externally
  useEffect(() => {
    if (!isFeatureFlagLoaded) {
      return;
    }

    const urlMode = searchParams.get("mode");
    const prevUrlMode = prevUrlModeRef.current;

    // Only sync when URL mode changes TO agent (external navigation)
    // Don't sync when URL already had agent mode (internal state changes)
    if (
      urlMode === ChatModeValues.Agent &&
      prevUrlMode !== ChatModeValues.Agent &&
      isAgentModeAccessible
    ) {
      setMode(ChatModeValues.Agent);
      clearMessages();
    }

    // Update the ref to track current URL mode
    prevUrlModeRef.current = urlMode;
  }, [searchParams, isFeatureFlagLoaded, isAgentModeAccessible, clearMessages]);

  // Sync mode state to URL param
  // Only shows ?mode=agent when agent mode is accessible AND currently in agent mode
  // Don't modify URL params until feature flag has loaded
  useEffect(() => {
    if (!isFeatureFlagLoaded) {
      return;
    }
    setSearchParams(
      (prev) => {
        const newParams = new URLSearchParams(prev);
        if (isAgentModeAccessible && isAgentMode) {
          newParams.set("mode", mode);
        } else {
          newParams.delete("mode");
        }
        return newParams;
      },
      { replace: true }
    );
  }, [
    mode,
    isAgentMode,
    isAgentModeAccessible,
    isFeatureFlagLoaded,
    setSearchParams,
  ]);

  // Load session from URL when sessionId changes
  // Only applies to agent mode (v2) - workflow mode (v1) doesn't support chat history
  useEffect(() => {
    // If in workflow mode and there's a sessionId in URL, remove it
    if (!isAgentMode && urlSessionId) {
      setSearchParams(
        (prev) => {
          const newParams = new URLSearchParams(prev);
          newParams.delete("sessionId");
          return newParams;
        },
        { replace: true }
      );
      return;
    }

    // If no session ID in URL (e.g. user started a new chat), reset the
    // loaded-session tracking so re-navigating to the same session will reload it.
    if (!urlSessionId) {
      loadedSessionIdRef.current = null;
      return;
    }

    // Skip if already loaded this session
    if (loadedSessionIdRef.current === urlSessionId) {
      return;
    }

    const loadSessionFromUrl = async () => {
      try {
        await handleOpenChat(urlSessionId);
        loadedSessionIdRef.current = urlSessionId;
      } catch (error) {
        console.error("Failed to load session from URL:", error);
        // Remove invalid sessionId from URL
        setSearchParams(
          (prev) => {
            const newParams = new URLSearchParams(prev);
            newParams.delete("sessionId");
            return newParams;
          },
          { replace: true }
        );
      }
    };

    loadSessionFromUrl();
  }, [urlSessionId, handleOpenChat, setSearchParams, isAgentMode]);

  // Update URL after first message is sent (new session starts)
  // Only applies to agent mode (v2) - workflow mode (v1) doesn't support chat history
  useEffect(() => {
    const prevCount = prevMessageCountRef.current;
    const currentCount = storeMessages.length;

    // Detect transition from 0 messages to >0 messages (first message sent)
    // Only update URL if we're not already on a session URL and in agent mode
    if (prevCount === 0 && currentCount > 0 && !urlSessionId && isAgentMode) {
      // Mark as loaded to prevent reloading when URL updates
      loadedSessionIdRef.current = sessionId;
      setSearchParams(
        (prev) => {
          const newParams = new URLSearchParams(prev);
          newParams.set("sessionId", sessionId);
          return newParams;
        },
        { replace: true }
      );
    }

    prevMessageCountRef.current = currentCount;
  }, [
    storeMessages.length,
    sessionId,
    urlSessionId,
    setSearchParams,
    isAgentMode,
  ]);

  /**
   * Opens the modal to confirm switching modes.
   */
  const handleOpenSwitchModeModal = () => {
    if (storeMessages.length === 0) {
      handleConfirmModeSwitch();
    } else {
      setIsSwitchModeModalOpen(true);
    }
  };

  /**
   * Closes the switch mode confirmation modal.
   */
  const handleCloseSwitchModeModal = () => {
    setIsSwitchModeModalOpen(false);
  };

  /**
   * Confirms the mode switch and closes the modal.
   * Mode is persisted to localStorage via the mode persistence effect.
   * Clears sessionId from URL to start fresh (v1 doesn't support chat history).
   */
  const handleConfirmModeSwitch = () => {
    const newMode = isAgentMode
      ? ChatModeValues.Workflow
      : ChatModeValues.Agent;
    setMode(newMode);
    setIsSwitchModeModalOpen(false);
    clearMessages();
    clearSources();
    clearAttachedSources();

    // Remove sessionId from URL to start a fresh session
    // This is especially important for v1 which doesn't support chat history
    setSearchParams(
      (prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.delete("sessionId");
        return newParams;
      },
      { replace: true }
    );
    // Reset loaded session tracking so a new session can be loaded if switching back
    loadedSessionIdRef.current = null;
  };

  // Page title based on the current mode
  const pageTitle = isAgentMode ? "Materiom AI v2" : "Materiom AI";

  return (
    <Authenticated>
      <title>{pageTitle} - Materiom Commons</title>
      {/* ThemeProvider applies the custom MUI theme to the page */}
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box
          sx={{
            display: "flex",
            width: "100%",
            height: "100vh",
            overflow: "hidden",
            bgcolor: "#F5F5F5",
            position: "relative",
          }}
        >
          {/* Main Content Area */}
          <Box
            sx={{
              flexGrow: 1,
              height: "100%",
              overflow: "auto",
              display: "flex",
              flexDirection: "column",
              p: 3,
            }}
          >
            {/* Chat Panel and New Sources Form */}
            <Box
              sx={{
                flexGrow: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              {/* Mode Toggle Button - shown when feature flag is enabled, hidden for enterprise users */}
              {isAgentModeAccessible && !isEnterprise && (
                <ModeToggleButton
                  isAgentMode={isAgentMode}
                  onOpenSwitchModal={handleOpenSwitchModeModal}
                />
              )}
              {/* Materiom AI Container */}
              <MateriomAIContainer
                input={chatInput}
                setInput={setChatInput}
                mode={mode}
              />
            </Box>
          </Box>
        </Box>

        {/* Switch Mode Modal */}
        <SwitchModeModal
          open={isSwitchModeModalOpen}
          isAgentMode={isAgentMode}
          onClose={handleCloseSwitchModeModal}
          onConfirm={handleConfirmModeSwitch}
        />

        {/* Agent Onboarding Survey - shown first for new users */}
        {agentOnboardingSurvey && (
          <MultiStepSurvey
            survey={agentOnboardingSurvey}
            open={isOnboardingSurveyVisible}
            responses={onboardingResponses}
            onResponseChange={setOnboardingResponse}
            onSubmit={submitOnboardingSurvey}
            onDismiss={dismissOnboardingSurvey}
            formTitle={MATERIOM_AI_V2_ONBOARDING_SURVEY.FORM_TITLE}
            lastPageTitle={MATERIOM_AI_V2_ONBOARDING_SURVEY.LAST_PAGE_TITLE}
            lastPageMessage={MATERIOM_AI_V2_ONBOARDING_SURVEY.LAST_PAGE_MESSAGE}
          />
        )}

        {/* Agent Exit/Feedback Survey - shown after onboarding is complete */}
        {agentExitSurvey && !isOnboardingSurveyVisible && (
          <MultiStepSurvey
            survey={agentExitSurvey}
            open={isFeedbackSurveyVisible}
            responses={feedbackResponses}
            onResponseChange={setFeedbackResponse}
            onSubmit={submitFeedbackSurvey}
            onDismiss={dismissFeedbackSurvey}
            formTitle={MATERIOM_AI_V2_FEEDBACK_SURVEY.FORM_TITLE}
            lastPageTitle={MATERIOM_AI_V2_FEEDBACK_SURVEY.LAST_PAGE_TITLE}
            lastPageMessage={MATERIOM_AI_V2_FEEDBACK_SURVEY.LAST_PAGE_MESSAGE}
          />
        )}
      </ThemeProvider>
    </Authenticated>
  );
}

export default AuthenticatedMateriomAI;
